import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    FiSearch, FiArrowLeft, FiClipboard, FiRepeat, FiZap, FiCalendar, FiRefreshCw,
    FiChevronLeft, FiChevronRight, FiX, FiHelpCircle, FiExternalLink,
} from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import {
    getWebhookCalls, type WebhookCallsPage, type WebhookCallType,
} from '../../services/webhookAnalyticsService';
import { webhookCallLink, webhookCallOpenLabel, webhookCallUnavailableReason } from './webhookCallLinks';
import '../../styles/MarketplacePage.css';
import '../../styles/Artifacts.css';
import '../../styles/Webhooks.css';

type TabKey = 'all' | 'commands' | 'routines';
type DatePreset = 'today' | 'week' | 'month';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const TAB_TO_TYPE: Record<TabKey, WebhookCallType> = { all: 'all', commands: 'command', routines: 'routine' };
const TABS: { id: TabKey; label: string; Icon: React.ComponentType<{ size?: number }>; color: string }[] = [
    { id: 'all', label: 'All', Icon: FiZap, color: '#f43f5e' },
    { id: 'commands', label: 'Commands', Icon: FiClipboard, color: '#6366f1' },
    { id: 'routines', label: 'Routines', Icon: FiRepeat, color: '#00b0d3' },
];

const STATUS_STYLE: Record<'done' | 'failed' | 'running', { label: string; color: string; bg: string; border: string }> = {
    done: { label: 'Done', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' },
    failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' },
    running: { label: 'Running', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
};

function parseTab(value: string | null): TabKey {
    return value === 'commands' || value === 'routines' ? value : 'all';
}

function toYMD(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local-midnight ISO for a YYYY-MM-DD picker value, optionally shifted by whole days. */
function ymdToIso(ymd: string, addDays = 0): string | undefined {
    if (!ymd) return undefined;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d + addDays).toISOString();
}

function formatCalledAt(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function statusKey(status: string): 'done' | 'failed' | 'running' {
    return status === 'DONE' ? 'done' : status === 'FAILED' ? 'failed' : 'running';
}

const WebhookReportPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = parseTab(searchParams.get('tab'));
    // The call shown in the right sidebar (WebhookCallDetailSidebar reads the same param).
    const selectedCallId = Number(searchParams.get('call')) || null;
    const selectCall = useCallback((id: number) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('call', String(id));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    // Page is remembered per filter set, so changing tab/search/dates lands on page 1
    // in the same render — no extra fetch for the stale page.
    const filterKey = `${activeTab}|${search}|${fromDate}|${toDate}`;
    const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
    const page = pageState.key === filterKey ? pageState.page : 1;
    const setPage = useCallback((next: number | ((p: number) => number)) => {
        setPageState((prev) => {
            const current = prev.key === filterKey ? prev.page : 1;
            return { key: filterKey, page: typeof next === 'function' ? next(current) : next };
        });
    }, [filterKey]);
    const [dateMenuOpen, setDateMenuOpen] = useState(false);
    const dateMenuRef = useRef<HTMLDivElement>(null);

    const [data, setData] = useState<WebhookCallsPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Debounce typing so each keystroke doesn't hit the API.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        getWebhookCalls({
            type: TAB_TO_TYPE[activeTab],
            fromDate: ymdToIso(fromDate),
            toDate: ymdToIso(toDate, 1),
            search,
            page,
            pageSize: PAGE_SIZE,
        }, controller.signal)
            .then((res) => {
                setData(res);
                // Filters can shrink the result set below the current page — snap back.
                if (res.totalPages < page) setPage(res.totalPages);
            })
            .catch((err) => {
                if (err?.name !== 'AbortError') setError('Could not load webhook calls. Please try again.');
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [activeTab, search, fromDate, toDate, page, refreshKey, setPage]);

    useEffect(() => {
        if (!dateMenuOpen) return;
        const onDown = (e: MouseEvent) => {
            if (dateMenuRef.current && !dateMenuRef.current.contains(e.target as Node)) setDateMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [dateMenuOpen]);

    const setTab = useCallback((tab: TabKey) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', tab);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const applyDatePreset = useCallback((preset: DatePreset) => {
        const now = new Date();
        const start = new Date(now);
        if (preset === 'week') start.setDate(start.getDate() - 6);
        else if (preset === 'month') start.setDate(start.getDate() - 29);
        setFromDate(toYMD(start));
        setToDate(toYMD(now));
    }, []);

    const clearDates = useCallback(() => {
        setFromDate('');
        setToDate('');
    }, []);

    const activeDatePreset: DatePreset | null = useMemo(() => {
        if (!fromDate || !toDate) return null;
        const now = new Date();
        if (toDate !== toYMD(now)) return null;
        if (fromDate === toYMD(now)) return 'today';
        const week = new Date(now); week.setDate(week.getDate() - 6);
        if (fromDate === toYMD(week)) return 'week';
        const month = new Date(now); month.setDate(month.getDate() - 29);
        if (fromDate === toYMD(month)) return 'month';
        return null;
    }, [fromDate, toDate]);

    const dateSummary = useMemo(() => {
        if (activeDatePreset === 'today') return 'Today';
        if (activeDatePreset === 'week') return 'Last Week';
        if (activeDatePreset === 'month') return 'Last Month';
        if (fromDate && toDate) return `${fromDate} → ${toDate}`;
        if (fromDate) return `From ${fromDate}`;
        if (toDate) return `Until ${toDate}`;
        return 'All dates';
    }, [activeDatePreset, fromDate, toDate]);

    const counts = data?.counts;
    const items = data?.items ?? [];
    const totalPages = data?.totalPages ?? 1;
    const totalCount = data?.totalCount ?? 0;
    const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);
    const tabCount = (id: TabKey) => (id === 'commands' ? counts?.commands : id === 'routines' ? counts?.routines : counts?.all) ?? 0;
    const hasFilters = !!(search || fromDate || toDate);

    return (
        <div style={{ padding: '24px 32px', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <button
                onClick={() => navigate(ROUTES.SETTINGS_WEBHOOKS)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, marginBottom: 16, padding: 0,
                }}
            >
                <FiArrowLeft size={13} /> Back to Webhooks
            </button>

            <div className="wh-report__toolbar">
                <div className="mkt-page__search-wrap" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                    <FiSearch size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                    <input
                        className="mkt-page__search-input"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search by name or command number..."
                    />
                    {searchInput && (
                        <button type="button" className="art-filter__clear" onClick={() => setSearchInput('')} title="Clear search" style={{ display: 'flex' }}>
                            <FiX size={13} />
                        </button>
                    )}
                </div>

                <div ref={dateMenuRef} style={{ position: 'relative' }}>
                    <button
                        type="button"
                        className={`art-filter__btn art-filter__btn--date${fromDate || toDate ? ' art-filter__btn--active' : ''}`}
                        title="Filter by date"
                        aria-haspopup="true"
                        aria-expanded={dateMenuOpen}
                        onClick={() => setDateMenuOpen((p) => !p)}
                    >
                        <FiCalendar size={12} />
                        <span className="art-filter__btn-label">{dateSummary}</span>
                    </button>

                    {dateMenuOpen && (
                        <div className="art-filter__popover art-filter__popover--date" role="menu" aria-label="Filter by date">
                            <div className="art-filter__type-header">
                                <span className="art-filter__label">Filter by date</span>
                                {(fromDate || toDate) && (
                                    <button type="button" className="art-filter__clear" onClick={clearDates}>Clear</button>
                                )}
                            </div>
                            <div className="art-filter__preset-pills">
                                {([
                                    { key: 'today', label: 'Today' },
                                    { key: 'week', label: 'Last Week' },
                                    { key: 'month', label: 'Last Month' },
                                ] as const).map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        className={`art-filter__preset-pill${activeDatePreset === p.key ? ' art-filter__preset-pill--active' : ''}`}
                                        onClick={() => applyDatePreset(p.key)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <div className="art-filter__custom-dates">
                                <div className="art-filter__date-row">
                                    <label className="art-filter__label">From</label>
                                    <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="art-filter__date" />
                                </div>
                                <div className="art-filter__date-row">
                                    <label className="art-filter__label">To</label>
                                    <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="art-filter__date" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="wh-report__icon-btn"
                    onClick={() => setRefreshKey((k) => k + 1)}
                    disabled={loading}
                    title="Refresh"
                >
                    <FiRefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
                </button>
            </div>

            <div className="wh-report__tabs" role="tablist">
                {TABS.map(({ id, label, Icon, color }) => {
                    const active = activeTab === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            className="wh-report__tab"
                            style={active ? { color, borderBottomColor: color } : undefined}
                            onClick={() => setTab(id)}
                        >
                            <Icon size={12} />
                            {label}
                            {counts && <span className="wh-report__tab-count">{tabCount(id)}</span>}
                        </button>
                    );
                })}
            </div>

            <div className="mcp-table" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div className="wh-table__header">
                    <div className="wh-table__header-cell">Name</div>
                    <div className="wh-table__header-cell">Type</div>
                    <div className="wh-table__header-cell">Status</div>
                    <div className="wh-table__header-cell">Called At</div>
                    <div className="wh-table__header-cell">Action</div>
                </div>

                {error ? (
                    <div className="wh-report__error">{error}</div>
                ) : loading && !data ? (
                    <div className="wh-empty">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="wh-empty">
                        {hasFilters ? 'No calls match these filters.' : `No ${activeTab === 'all' ? 'calls' : activeTab} yet.`}
                    </div>
                ) : items.map((item) => {
                    const isRoutine = item.type === 'ROUTINE';
                    const link = webhookCallLink(item);
                    const unavailable = webhookCallUnavailableReason(item);
                    const selected = selectedCallId === item.id;
                    const st = STATUS_STYLE[statusKey(item.status)];
                    const title = item.title || item.taskNumber || (isRoutine ? 'Routine run' : 'Command');
                    const failedReason = item.status === 'FAILED' && item.errorMessage ? item.errorMessage : null;
                    // Deleted targets get a "?" next to the title with this text on hover,
                    // rather than another line under the name.
                    const deletedTip = item.targetDeleted
                        ? `This ${isRoutine ? 'routine' : 'command'} has been deleted and can no longer be opened.`
                        : null;
                    const sub = !failedReason && !isRoutine && item.taskNumber && item.taskNumber !== title ? item.taskNumber : null;
                    return (
                        <div
                            key={item.id}
                            className={`wh-table__row wh-table__row--link${selected ? ' wh-table__row--selected' : ''}`}
                            style={{ opacity: loading ? 0.6 : 1 }}
                            onClick={() => selectCall(item.id)}
                            title="View call details"
                            aria-selected={selected}
                        >
                            <div className="wh-report__name">
                                <div className="mcp-icon-box">
                                    {isRoutine ? <FiRepeat size={14} /> : <FiClipboard size={14} />}
                                </div>
                                <div className="wh-report__name-text">
                                    <span className="wh-report__title-row">
                                        <span className="wh-report__title">{title}</span>
                                        {deletedTip && (
                                            <span className="wh-report__help" tabIndex={0} aria-label={deletedTip}>
                                                <FiHelpCircle size={12} />
                                                <span className="wh-report__help-tip" role="tooltip">{deletedTip}</span>
                                            </span>
                                        )}
                                    </span>
                                    {failedReason && (
                                        <span className="wh-report__fail-badge" title={`Failure Reason: ${failedReason}`}>
                                            <span className="wh-report__fail-badge-label">Failure Reason:</span> {failedReason}
                                        </span>
                                    )}
                                    {sub && <span className="wh-report__sub">{sub}</span>}
                                </div>
                            </div>
                            <div className="wh-report__cell">{isRoutine ? 'Routine' : 'Command'}</div>
                            <div>
                                <span className="wh-status-badge" style={{ color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>
                                    <span className="wh-status-dot" />
                                    {st.label}
                                </span>
                            </div>
                            <div className="wh-report__cell">{formatCalledAt(item.createdAt)}</div>
                            <div>
                                <button
                                    type="button"
                                    className="wh-report__open-btn"
                                    disabled={!link}
                                    title={unavailable ?? undefined}
                                    onClick={(e) => { e.stopPropagation(); if (link) navigate(link); }}
                                >
                                    <FiExternalLink size={12} /> {webhookCallOpenLabel(item)}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {totalPages > 1 && (
                <div className="wh-pagination">
                    <span>Showing {rangeStart}–{rangeEnd} of {totalCount}</span>
                    <div className="wh-pagination__controls">
                        <button type="button" className="wh-report__icon-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} aria-label="Previous page">
                            <FiChevronLeft size={14} />
                        </button>
                        <span>Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
                        <button type="button" className="wh-report__icon-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} aria-label="Next page">
                            <FiChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebhookReportPage;
